import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toApiError } from '../../api/client';
import { organisationApi } from '../../api/endpoints';
import { useAuth } from '../../auth/AuthContext';
import type {
  BranchEmployee,
  BranchSummary,
  OrganisationDashboard,
  StaffBranchAssignment,
} from '../../api/types';
import { ConfirmDialog, Field } from '../../components/Common';
import { EmptyState, ErrorState, InlineError, InlineSuccess, TableSkeleton } from '../../components/States';
import { dateTime } from '../../utils/format';

/**
 * Front-desk accounts for one organisation.
 *
 * These are the credentials staff use to sign in with the organisation name and a
 * username. Passwords are never shown or returned; the owner can set a new one, which
 * ends the employee's existing sessions, but cannot read the current one.
 */
export function BranchStaffPage() {
  const { businessId = '' } = useParams();
  const { isOwner } = useAuth();

  const [dashboard, setDashboard] = useState<OrganisationDashboard | null>(null);
  const [employees, setEmployees] = useState<BranchEmployee[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [staffForm, setStaffForm] = useState<'create' | 'assign' | null>(null);
  const activeBranches = dashboard?.branches.filter((branch) => !branch.isArchived) ?? [];

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [org, staff] = await Promise.all([
        organisationApi.dashboard(businessId),
        organisationApi.employees(businessId),
      ]);

      setDashboard(org);
      setEmployees(staff);
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !employees) return <TableSkeleton rows={4} />;
  if (error && !employees) return <ErrorState message={error} onRetry={load} />;

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h1 className="h4 mb-1">Staff accounts</h1>
          <p className="sf-muted small mb-0">
            {dashboard?.organisationName} · staff sign in with the organisation name, their username and password.
          </p>
        </div>

        <div className="d-flex gap-2">
          <Link to="/owner/branches" className="btn btn-outline-secondary">
            Back to branches
          </Link>
          {isOwner && activeBranches.length > 0 && (
            <>
              <button type="button" className="btn btn-outline-primary" onClick={() => setStaffForm('assign')}>
                Assign existing staff
              </button>
              <button type="button" className="btn btn-primary" onClick={() => setStaffForm('create')}>
                Create new staff
              </button>
            </>
          )}
        </div>
      </div>

      <InlineError message={error} />
      <InlineSuccess message={success} />

      {employees && employees.length === 0 ? (
        <EmptyState
          title="No staff accounts yet"
          description="Add an account so your front desk can update availability and reply to enquiries without using your own login."
        />
      ) : (
        <div className="sf-card">
          <div className="table-responsive">
            <table className="table mb-0 align-middle">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Username</th>
                  <th scope="col">Assigned branches</th>
                  <th scope="col">Last signed in</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {(employees ?? []).map((employee) => (
                  <EmployeeRow
                    key={employee.membershipId}
                    businessId={businessId}
                    employee={employee}
                    isOwner={isOwner}
                    onChanged={(message) => {
                      setSuccess(message);
                      setError(null);
                      void load();
                    }}
                    onError={(message) => {
                      setError(message);
                      setSuccess(null);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isOwner && staffForm === 'create' && dashboard && (
        <AddEmployeeForm
          businessId={businessId}
          branches={activeBranches}
          onClose={() => setStaffForm(null)}
          onCreated={(message) => {
            setStaffForm(null);
            setSuccess(message);
            setError(null);
            void load();
          }}
          onError={(message) => {
            setError(message);
            setSuccess(null);
          }}
        />
      )}

      {isOwner && staffForm === 'assign' && dashboard && employees && (
        <AssignEmployeeForm
          businessId={businessId}
          employees={employees}
          branches={activeBranches}
          onClose={() => setStaffForm(null)}
          onAssigned={(message) => {
            setStaffForm(null);
            setSuccess(message);
            setError(null);
            void load();
          }}
          onError={(message) => {
            setError(message);
            setSuccess(null);
          }}
        />
      )}
    </>
  );
}

function EmployeeRow({
  businessId,
  employee,
  isOwner,
  onChanged,
  onError,
}: {
  businessId: string;
  employee: BranchEmployee;
  isOwner: boolean;
  onChanged: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [removing, setRemoving] = useState<StaffBranchAssignment | null>(null);
  const [username, setUsername] = useState(employee.username);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (action: () => Promise<unknown>, message: string) => {
    setBusy(true);

    try {
      await action();
      onChanged(message);
    } catch (err) {
      onError(toApiError(err).message);
    } finally {
      setBusy(false);
      setRenaming(false);
      setResetting(false);
      setDeactivating(false);
      setPassword('');
    }
  };

  return (
    <>
      <tr className={employee.isActive ? undefined : 'opacity-50'}>
        <td>
          {employee.fullName}
          {!employee.isActive && <span className="sf-pill sf-pill--warn ms-2">Deactivated</span>}
        </td>
        <td>
          <code>{employee.username}</code>
        </td>
        <td>
          {employee.assignedBranches.length === 0 ? (
            <span className="sf-muted">No active branch assignments</span>
          ) : (
            <ul className="list-unstyled mb-0">
              {employee.assignedBranches.map((assignment) => (
                <li key={assignment.assignmentId} className="d-flex align-items-center gap-2 mb-1">
                  <span>{assignment.branchName}</span>
                  {isOwner && (
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger p-0"
                      disabled={busy}
                      onClick={() => setRemoving(assignment)}
                      aria-label={`Remove ${employee.fullName} from ${assignment.branchName}`}
                    >
                      Remove
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </td>
        <td className="sf-muted small">
          {employee.lastLoginAtUtc ? dateTime(employee.lastLoginAtUtc) : 'Never'}
        </td>
        <td className="text-end">
          {isOwner && (
            <div className="btn-group btn-group-sm">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setRenaming(true)}>
                Username
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setResetting(true)}>
                Password
              </button>
              <button
                type="button"
                className={`btn btn-outline-${employee.isActive ? 'danger' : 'primary'}`}
                onClick={() =>
                  employee.isActive
                    ? setDeactivating(true)
                    : run(
                        () => organisationApi.setActive(businessId, employee.membershipId, true),
                        `${employee.fullName} can sign in again.`,
                      )
                }
              >
                {employee.isActive ? 'Deactivate' : 'Reactivate'}
              </button>
            </div>
          )}
        </td>
      </tr>

      {isOwner && renaming && (
        <tr>
          <td colSpan={5}>
            <form
              className="d-flex flex-wrap gap-2 align-items-end"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                void run(
                  () => organisationApi.changeUsername(businessId, employee.membershipId, username.trim()),
                  `Username changed to ${username.trim()}.`,
                );
              }}
            >
              <Field
                label="New username"
                htmlFor={`username-${employee.membershipId}`}
                hint="Unique within your organisation. Letters, digits, dots, hyphens and underscores."
              >
                <input
                  id={`username-${employee.membershipId}`}
                  className="form-control"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </Field>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                Save
              </button>
              <button type="button" className="btn btn-link" onClick={() => setRenaming(false)}>
                Cancel
              </button>
            </form>
          </td>
        </tr>
      )}

      {isOwner && resetting && (
        <tr>
          <td colSpan={5}>
            <form
              className="d-flex flex-wrap gap-2 align-items-end"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                void run(
                  () => organisationApi.resetPassword(businessId, employee.membershipId, password),
                  `A new password is set for ${employee.fullName}. They have been signed out everywhere.`,
                );
              }}
            >
              <Field
                label="New password"
                htmlFor={`password-${employee.membershipId}`}
                hint="At least 8 characters, with a digit, a capital and a small letter. Give it to them yourself; it is never shown again."
              >
                <input
                  id={`password-${employee.membershipId}`}
                  type="password"
                  className="form-control"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </Field>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                Set password
              </button>
              <button type="button" className="btn btn-link" onClick={() => setResetting(false)}>
                Cancel
              </button>
            </form>
          </td>
        </tr>
      )}

      {isOwner && (
        <ConfirmDialog
          open={removing !== null}
          title={`Remove ${employee.fullName} from ${removing?.branchName ?? 'this branch'}?`}
          message="This removes only this branch assignment. The staff account and any other branch assignments remain."
          confirmLabel="Remove assignment"
          destructive
          busy={busy}
          onCancel={() => setRemoving(null)}
          onConfirm={() => {
            if (!removing) return;
            void run(
              () =>
                organisationApi.removeEmployeeAssignment(
                  businessId,
                  employee.membershipId,
                  removing.branchPropertyId,
                ),
              `${employee.fullName} was removed from ${removing.branchName}. Their account and other assignments remain.`,
            );
            setRemoving(null);
          }}
        />
      )}

      {isOwner && (
        <ConfirmDialog
          open={deactivating}
          title={`Deactivate ${employee.fullName}?`}
          message="They are signed out immediately and cannot sign in again until you reactivate the account. Nothing they have done is deleted."
          confirmLabel="Deactivate"
          destructive
          busy={busy}
          onCancel={() => setDeactivating(false)}
          onConfirm={() =>
            run(
              () => organisationApi.setActive(businessId, employee.membershipId, false, 'Deactivated by the owner.'),
              `${employee.fullName} has been deactivated.`,
            )
          }
        />
      )}
    </>
  );
}

function AddEmployeeForm({
  businessId,
  branches,
  onClose,
  onCreated,
  onError,
}: {
  businessId: string;
  branches: BranchSummary[];
  onClose: () => void;
  onCreated: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [branchId, setBranchId] = useState(branches[0]?.id ?? '');
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [canPublish, setCanPublish] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);

    try {
      await organisationApi.createEmployee(businessId, branchId, {
        fullName: fullName.trim(),
        username: username.trim(),
        password,
        canManageProperties: true,
        canPublishListings: canPublish,
      });

      onCreated(`${fullName.trim()} can now sign in with the username ${username.trim()}.`);
    } catch (err) {
      onError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sf-card sf-card-pad mt-3">
      <h2 className="h6 mb-3">Add a staff account</h2>

      <form onSubmit={submit} noValidate>
        <Field label="Branch" htmlFor="staff-branch" required>
          <select
            id="staff-branch"
            className="form-select"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            required
          >
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Their name" htmlFor="staff-name" required>
          <input
            id="staff-name"
            className="form-control"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
          />
        </Field>

        <Field
          label="Username"
          htmlFor="staff-username"
          required
          hint="What they type on the login screen, alongside your organisation name."
        >
          <input
            id="staff-username"
            className="form-control"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="off"
            required
          />
        </Field>

        <Field
          label="Password"
          htmlFor="staff-password"
          required
          hint="At least 8 characters, with a digit, a capital and a small letter."
        >
          <input
            id="staff-password"
            type="password"
            className="form-control"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
        </Field>

        <div className="form-check mb-3">
          <input
            id="staff-publish"
            className="form-check-input"
            type="checkbox"
            checked={canPublish}
            onChange={(e) => setCanPublish(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="staff-publish">
            May submit the branch for review
          </label>
          <div className="form-text">
            Billing stays with you. Staff accounts can never buy or renew a subscription.
          </div>
        </div>

        <div className="d-flex gap-2">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Creating…' : 'Create account'}
          </button>
          <button type="button" className="btn btn-link" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function AssignEmployeeForm({
  businessId,
  employees,
  branches,
  onClose,
  onAssigned,
  onError,
}: {
  businessId: string;
  employees: BranchEmployee[];
  branches: BranchSummary[];
  onClose: () => void;
  onAssigned: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [branchId, setBranchId] = useState(branches[0]?.id ?? '');
  const [membershipId, setMembershipId] = useState('');
  const [busy, setBusy] = useState(false);
  const eligibleEmployees = employees.filter(
    (employee) =>
      employee.isActive &&
      !employee.assignedBranches.some((assignment) => assignment.branchPropertyId === branchId),
  );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);

    try {
      await organisationApi.assignEmployee(businessId, branchId, membershipId);
      const employee = employees.find((item) => item.membershipId === membershipId);
      onAssigned(`${employee?.fullName ?? 'Staff member'} was assigned to ${branches.find((b) => b.id === branchId)?.name ?? 'the branch'}.`);
    } catch (err) {
      onError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sf-card sf-card-pad mt-3">
      <h2 className="h6 mb-3">Assign an existing staff account</h2>
      <p className="sf-muted small">
        Select an active staff account already belonging to this business. This adds a branch assignment
        without creating another account or changing their login credentials.
      </p>

      <form onSubmit={submit} noValidate>
        <Field label="Destination branch" htmlFor="assign-staff-branch" required>
          <select
            id="assign-staff-branch"
            className="form-select"
            value={branchId}
            onChange={(event) => {
              setBranchId(event.target.value);
              setMembershipId('');
            }}
            required
          >
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>{branch.name}</option>
            ))}
          </select>
        </Field>

        <Field
          label="Existing staff account"
          htmlFor="assign-staff-membership"
          required
          hint={eligibleEmployees.length === 0
            ? 'No active, unassigned staff accounts are available for this branch.'
            : 'The list contains staff accounts from this business only. Already assigned accounts are excluded.'}
        >
          <select
            id="assign-staff-membership"
            className="form-select"
            value={membershipId}
            onChange={(event) => setMembershipId(event.target.value)}
            required
            disabled={eligibleEmployees.length === 0}
          >
            <option value="">Choose staff</option>
            {eligibleEmployees.map((employee) => (
              <option key={employee.membershipId} value={employee.membershipId}>
                {employee.username} · {employee.fullName} · currently assigned to{' '}
                {employee.assignedBranches.length === 0
                  ? 'no branches'
                  : employee.assignedBranches.map((assignment) => assignment.branchName).join(', ')}
              </option>
            ))}
          </select>
        </Field>

        <div className="d-flex gap-2">
          <button type="submit" className="btn btn-primary" disabled={busy || eligibleEmployees.length === 0}>
            {busy ? 'Assigning…' : 'Assign to branch'}
          </button>
          <button type="button" className="btn btn-link" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
